import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api';
import { MapPinned } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useMachinesOverview from '../hooks/useMachinesOverview';
import { NO_COMPANY } from '../components/CompanyScopePicker';
import styles from './MachineList.module.scss';

const center = { lat: 59.3293, lng: 18.0686 };
const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const googleMapsMapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || '';

// Color constants for device status markers
const COLORS = {
  Gray: '#9e9e9e',
  GrayBorder: '#757575',
  Yellow: '#ffeb3b',
  YellowBorder: '#f57c00',
  Green: '#4caf50',
  GreenBorder: '#388e3c',
};

function MapState({ title, description }) {
  return (
    <div className={styles.map} role="status">
      <div className={styles.mapPlaceholder}>
        <span className={styles.mapPlaceholderIcon}>
          <MapPinned size={22} />
        </span>
        <strong>{title}</strong>
        <span>{description}</span>
      </div>
    </div>
  );
}

function GoogleMapsView({ onDeviceSelect, selectedCompanyIds = [] }) {
  const [map, setMap] = useState(null);
  const markersRef = useRef([]);
  const hasAutoAdjustedViewRef = useRef(false);
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const navigate = useNavigate();

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey,
    libraries: ['marker'],
  });

  // Locations, status and online flags all come from the shared overview
  // poll (one request per 30 seconds) instead of per-device fetches.
  const overview = useMachinesOverview();

  const deviceLocations = useMemo(() => {
    const machines = overview?.machines ?? [];
    // Mirror the machine selector's Company scope filter so the map shows the
    // same subset. Empty selection = all companies.
    const companyFiltered =
      selectedCompanyIds.length === 0
        ? machines
        : machines.filter(machine => selectedCompanyIds.includes(machine.company_id || NO_COMPANY));
    return companyFiltered
      .map(machine => {
        const location = machine.location;
        if (!location || typeof location.lat !== 'number' || typeof location.lng !== 'number') {
          return null;
        }

        return {
          id: machine.id,
          serial: machine.serial_number,
          name: machine.nickname || machine.serial_number,
          lat: location.lat,
          lng: location.lng,
          timestamp: typeof location.timestamp === 'number' ? location.timestamp : null,
          canbusClients:
            typeof machine.canbus_clients === 'number' && !isNaN(machine.canbus_clients)
              ? machine.canbus_clients
              : 0,
          gnssClients:
            typeof machine.gnss_clients === 'number' && !isNaN(machine.gnss_clients)
              ? machine.gnss_clients
              : 0,
          isOnline: Boolean(machine.is_online),
        };
      })
      .filter(Boolean);
  }, [overview, selectedCompanyIds]);

  // Re-fit the map to the visible machines whenever the company scope changes,
  // so filtering to a company always brings its machines into view.
  useEffect(() => {
    hasAutoAdjustedViewRef.current = false;
  }, [selectedCompanyIds]);

  const onLoad = useCallback(mapInstance => {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Render advanced markers once the map and marker library are ready
  useEffect(() => {
    if (!isLoaded || !map) {
      return undefined;
    }

    let cancelled = false;

    const addMarkers = async () => {
      try {
        const { AdvancedMarkerElement, PinElement } =
          await window.google.maps.importLibrary('marker');
        const baseZIndex = 1;
        const hoverZIndex = 1000000;

        markersRef.current.forEach(marker => {
          if (marker) {
            if (typeof marker._wrapperCleanup === 'function') {
              marker._wrapperCleanup();
            }
            marker.map = null;
          }
        });

        if (cancelled) {
          return;
        }

        // Auto-adjust map view only once on first successful data load.
        if (!hasAutoAdjustedViewRef.current && deviceLocations.length > 0) {
          if (deviceLocations.length > 1) {
            const bounds = new window.google.maps.LatLngBounds();
            deviceLocations.forEach(device => {
              bounds.extend({ lat: device.lat, lng: device.lng });
            });

            const padding = { top: 50, right: 50, bottom: 50, left: 50 };
            map.fitBounds(bounds, padding);
          } else {
            map.setCenter({ lat: deviceLocations[0].lat, lng: deviceLocations[0].lng });
            map.setZoom(10);
          }
          hasAutoAdjustedViewRef.current = true;
        }

        markersRef.current = deviceLocations.map(device => {
          // Determine marker color based on status
          let markerColor, borderColor;
          if (device.isOnline) {
            // Check if both canbus_clients and gnss_clients are > 0
            if (device.canbusClients > 0 && device.gnssClients > 0) {
              markerColor = COLORS.Green;
              borderColor = COLORS.GreenBorder;
            } else {
              markerColor = COLORS.Yellow;
              borderColor = COLORS.YellowBorder;
            }
          } else {
            markerColor = COLORS.Gray;
            borderColor = COLORS.GrayBorder;
          }

          const pin = new PinElement({
            background: markerColor,
            borderColor: borderColor,
            glyphColor: '#ffffff',
          });

          const markerWrapper = document.createElement('div');
          markerWrapper.className = styles.markerWrapper;
          markerWrapper.appendChild(pin.element);

          let markerInstance = null;

          const handleEnter = () => {
            if (markerInstance) {
              markerInstance.zIndex = hoverZIndex;
            }
            setHoveredMarker({
              id: device.id ?? device.serial,
              name: device.name,
              serial: device.serial,
              position: { lat: device.lat, lng: device.lng },
            });
          };

          const handleLeave = () => {
            if (markerInstance) {
              markerInstance.zIndex = baseZIndex;
            }
            setHoveredMarker(current => {
              if (current && (current.id === device.id || current.serial === device.serial)) {
                return null;
              }
              return current;
            });
          };

          const handleClick = () => {
            if (typeof onDeviceSelect === 'function') {
              onDeviceSelect(device.serial);
            }
            navigate(`/device/${device.serial}/status`);
          };

          markerWrapper.addEventListener('pointerenter', handleEnter);
          markerWrapper.addEventListener('pointerleave', handleLeave);
          markerWrapper.addEventListener('click', handleClick);

          const marker = new AdvancedMarkerElement({
            map,
            position: { lat: device.lat, lng: device.lng },
            content: markerWrapper,
            zIndex: baseZIndex,
          });

          markerInstance = marker;
          marker._wrapperCleanup = () => {
            markerWrapper.removeEventListener('pointerenter', handleEnter);
            markerWrapper.removeEventListener('pointerleave', handleLeave);
            markerWrapper.removeEventListener('click', handleClick);
          };

          return marker;
        });
      } catch (error) {
        console.error('Error loading advanced markers:', error);
      }
    };

    addMarkers();

    return () => {
      cancelled = true;
      markersRef.current.forEach(marker => {
        if (marker) {
          if (typeof marker._wrapperCleanup === 'function') {
            marker._wrapperCleanup();
          }
          marker.map = null;
        }
      });
      setHoveredMarker(null);
    };
  }, [deviceLocations, isLoaded, map, navigate, onDeviceSelect]);

  if (loadError) {
    return (
      <MapState
        title="Google Maps could not be loaded"
        description="Check the API key restrictions and network access, then rebuild the frontend."
      />
    );
  }

  return (
    <div className={styles.map}>
      {isLoaded ? (
        <GoogleMap
          mapContainerClassName={styles.googleMap}
          defaultCenter={center}
          defaultZoom={10}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            mapId: googleMapsMapId,
          }}
        >
          {hoveredMarker && (
            <OverlayView
              position={hoveredMarker.position}
              mapPaneName={OverlayView.FLOAT_PANE}
              getPixelPositionOffset={(width, height) => ({
                x: -width / 2,
                y: -height - 16,
              })}
            >
              <div className={styles.mapHoverCard}>
                <div className={styles.mapHoverName}>{hoveredMarker.name}</div>
                <div className={styles.mapHoverSerial}>{hoveredMarker.serial}</div>
              </div>
            </OverlayView>
          )}
        </GoogleMap>
      ) : (
        <div className={styles.mapPlaceholder}>Loading Google Maps…</div>
      )}
    </div>
  );
}

function MapShow({ onDeviceSelect, selectedCompanyIds }) {
  return <GoogleMapsView onDeviceSelect={onDeviceSelect} selectedCompanyIds={selectedCompanyIds} />;
}

export default MapShow;
